// Pure placement logic for the canvas — the framework "brain", DOM-free and
// unit-tested. The zustand store (canvas.ts) holds state and calls these; the
// grid (react-grid-layout) is just a view over the result.
//
// A PlacedWidget is the store's model of one widget on the canvas. It carries an
// `instanceId` (so the same widget can appear twice, each with a stable React /
// grid key) that is NOT persisted — the backend stores only `{ widgetKey,
// position }` (WidgetPlacement). We strip the id on save and re-mint it on load.

import { clampToGrid, GRID_COLUMNS } from "@catbento/shared";
import type { GridPosition, WidgetPlacement } from "@catbento/shared";

/** One widget on the canvas, as the store models it. */
export interface PlacedWidget {
  /** Stable per-instance id — the grid item key. Session-only, never persisted. */
  instanceId: string;
  widgetKey: string;
  position: GridPosition;
}

/** One item as react-grid-layout hands it back from a drag/resize. */
export interface RglLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Pick a position for a NEW widget: full default size, dropped at the bottom of
 * the canvas (x = 0, y = below everything) so it never lands on top of an
 * existing widget. Clamped to the grid. Pure.
 */
export function placeNewWidget(
  existing: PlacedWidget[],
  size: { w: number; h: number },
  columns: number = GRID_COLUMNS
): GridPosition {
  const bottom = existing.reduce((max, p) => Math.max(max, p.position.y + p.position.h), 0);
  return clampToGrid({ x: 0, y: bottom, w: size.w, h: size.h }, columns);
}

/**
 * Apply a react-grid-layout change back onto the placed widgets: match each
 * layout item to its widget by `instanceId` and update the position (clamped).
 * Widgets missing from the layout are left untouched (defensive — RGL always
 * returns them all). Pure — the store just swaps in the result.
 */
export function mergeRglLayout(
  placed: PlacedWidget[],
  layout: RglLayoutItem[],
  columns: number = GRID_COLUMNS
): PlacedWidget[] {
  const byId = new Map(layout.map((l) => [l.i, l]));
  return placed.map((p) => {
    const l = byId.get(p.instanceId);
    if (!l) return p;
    return { ...p, position: clampToGrid({ x: l.x, y: l.y, w: l.w, h: l.h }, columns) };
  });
}

/**
 * The pixel width of one grid column, matching react-grid-layout's own formula
 * (`calcGridColWidth`): the container width minus the inter-column margins and
 * the two side paddings, divided by the column count. Used to paint the
 * background grid so its lines line up with where widgets actually snap. Pure —
 * returns 0 for a not-yet-measured (0) width so the caller can skip drawing.
 */
export function gridColWidth(
  containerWidth: number,
  cols: number,
  marginX: number,
  paddingX: number
): number {
  if (containerWidth <= 0) return 0;
  return (containerWidth - marginX * (cols - 1) - paddingX * 2) / cols;
}

/** Strip to the persisted shape (drop instanceId) for PUT /layouts. Pure. */
export function toPlacements(placed: PlacedWidget[]): WidgetPlacement[] {
  return placed.map((p) => ({ widgetKey: p.widgetKey, position: p.position }));
}

/**
 * Re-hydrate placed widgets from a loaded board, minting a fresh instanceId for
 * each (positions are already grid-clamped by the backend). `genId` is injected
 * so tests stay deterministic and the module needs no crypto/DOM. Pure.
 */
export function fromPlacements(
  placements: WidgetPlacement[],
  genId: () => string
): PlacedWidget[] {
  return placements.map((p) => ({ instanceId: genId(), widgetKey: p.widgetKey, position: p.position }));
}
